import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AppCacheProvider } from "./lib/appcache";
import { AuthProvider } from "./lib/auth";
import { FavoritesProvider } from "./lib/favorites";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Home from "./pages/Home";

// Pagine ISF lazy: il login non scarica PDF viewer, Impara, Questionari e AI.
const Prodotti = lazy(() => import(/* webpackChunkName: "isf" */ "./pages/Prodotti"));
const ProductDetail = lazy(() => import(/* webpackChunkName: "isf" */ "./pages/ProductDetail"));
const Azienda = lazy(() => import(/* webpackChunkName: "isf" */ "./pages/Azienda"));
const Cerca = lazy(() => import(/* webpackChunkName: "isf" */ "./pages/Cerca"));
const Preferiti = lazy(() => import(/* webpackChunkName: "isf" */ "./pages/Preferiti"));
const MaterialDetail = lazy(() => import(/* webpackChunkName: "pdf-viewer" */ "./pages/MaterialDetail"));
const Impara = lazy(() => import(/* webpackChunkName: "impara" */ "./pages/Impara"));
const ImparaProdotto = lazy(() => import(/* webpackChunkName: "impara" */ "./pages/ImparaProdotto"));
const ImparaFlashcards = lazy(() => import(/* webpackChunkName: "impara" */ "./pages/ImparaFlashcards"));
const ImparaQuizRunner = lazy(() => import(/* webpackChunkName: "impara" */ "./pages/ImparaQuizRunner"));
const ImparaRipassa = lazy(() => import(/* webpackChunkName: "impara" */ "./pages/ImparaRipassa"));
const ImparaProgressi = lazy(() => import(/* webpackChunkName: "impara" */ "./pages/ImparaProgressi"));
const ImparaAI = lazy(() => import(/* webpackChunkName: "impara-ai" */ "./pages/ImparaAI"));
const Questionari = lazy(() => import(/* webpackChunkName: "questionari" */ "./pages/Questionari"));
const QuestionarioCompile = lazy(() => import(/* webpackChunkName: "questionari" */ "./pages/QuestionarioCompile"));
const SuggerimentiCompile = lazy(() => import(/* webpackChunkName: "questionari" */ "./pages/SuggerimentiCompile"));
const TestRunner = lazy(() => import(/* webpackChunkName: "questionari" */ "./pages/TestRunner"));

// Admin pages — lazy-loaded. An ISF never downloads these bundles.
// Grouped into one webpack chunk so clicking Admin triggers a single
// network fetch instead of many.
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminLayout"));
const AdminMateriali = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminMateriali"));
const AdminProdotti = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminProdotti"));
const AdminUtenti = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminUtenti"));
const AdminCategorie = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminCategorie"));
const AdminAnalytics = lazy(() => import(/* webpackChunkName: "admin-analytics" */ "./pages/admin/AdminAnalytics"));
const AdminLearningLayout = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminLearningLayout"));
const AdminFlashcards = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminFlashcards"));
const AdminQuizzes = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminQuizzes"));
const AdminTests = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminTests"));
const AdminLearningAnalytics = lazy(() => import(/* webpackChunkName: "admin-analytics" */ "./pages/admin/AdminLearningAnalytics"));
const AdminAI = lazy(() => import(/* webpackChunkName: "admin-ai" */ "./pages/admin/AdminAI"));
const AdminCestino = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminCestino"));
const AdminCartelle = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminCartelle"));
const AdminQuestionari = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminQuestionari"));
const AdminQuestionarioEditor = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminQuestionarioEditor"));
const AdminQuestionarioAssignments = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminQuestionarioAssignments"));
const AdminQuestionarioResults = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminQuestionarioResults"));

function AdminFallback() {
  return (
    <div className="space-y-3 p-4" aria-busy="true" aria-label="Caricamento area amministrazione">
      <div className="h-6 w-48 animate-pulse rounded bg-[#F7F9FC]" />
      <div className="h-24 animate-pulse rounded-xl bg-[#F7F9FC]" />
      <div className="h-24 animate-pulse rounded-xl bg-[#F7F9FC]" />
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <AppCacheProvider>
        <AuthProvider>
          <FavoritesProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors closeButton />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Home />} />
                <Route path="/prodotti" element={<Prodotti />} />
                <Route path="/prodotti/:id" element={<ProductDetail />} />
                <Route path="/azienda" element={<Azienda />} />
                <Route path="/materiali/:id" element={<MaterialDetail />} />
                <Route path="/cerca" element={<Cerca />} />
                <Route path="/preferiti" element={<Preferiti />} />

                <Route path="/impara" element={<Impara />} />
                {/* Maven AI chat: temporarily ADMIN-only (gate at route + backend). */}
                <Route
                  path="/impara/ai"
                  element={
                    <ProtectedRoute adminOnly>
                      <ImparaAI />
                    </ProtectedRoute>
                  }
                />
                <Route path="/impara/prodotti/:pid" element={<ImparaProdotto />} />
                <Route path="/impara/flashcards/:sid" element={<ImparaFlashcards />} />
                <Route path="/impara/quiz/:id" element={<ImparaQuizRunner kind="quiz" />} />
                <Route path="/impara/test/:id" element={<LegacyTestRedirect />} />
                <Route path="/impara/ripassa" element={<ImparaRipassa />} />
                <Route path="/impara/progressi" element={<ImparaProgressi />} />

                <Route path="/questionari" element={<Questionari />} />
                <Route path="/questionari/suggerimenti" element={<SuggerimentiCompile />} />
                <Route path="/questionari/test/:id" element={<TestRunner />} />
                <Route path="/questionari/:cid" element={<QuestionarioCompile />} />

                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <Suspense fallback={<AdminFallback />}>
                        <AdminLayout />
                      </Suspense>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/materiali" replace />} />
                  <Route path="materiali" element={<Suspense fallback={<AdminFallback />}><AdminMateriali /></Suspense>} />
                  <Route path="cartelle" element={<Suspense fallback={<AdminFallback />}><AdminCartelle /></Suspense>} />
                  <Route path="prodotti" element={<Suspense fallback={<AdminFallback />}><AdminProdotti /></Suspense>} />
                  <Route path="utenti" element={<Suspense fallback={<AdminFallback />}><AdminUtenti /></Suspense>} />
                  <Route path="categorie" element={<Suspense fallback={<AdminFallback />}><AdminCategorie /></Suspense>} />
                  <Route path="analytics" element={<Suspense fallback={<AdminFallback />}><AdminAnalytics /></Suspense>} />
                  <Route path="confronto" element={<Navigate to="/admin/analytics?tab=confronto" replace />} />
                  <Route path="ranking" element={<Navigate to="/admin/analytics?tab=ranking" replace />} />
                  <Route path="impara" element={<Suspense fallback={<AdminFallback />}><AdminLearningLayout /></Suspense>}>
                    <Route index element={<Navigate to="/admin/impara/flashcards" replace />} />
                    <Route path="flashcards" element={<Suspense fallback={<AdminFallback />}><AdminFlashcards /></Suspense>} />
                    <Route path="quizzes" element={<Suspense fallback={<AdminFallback />}><AdminQuizzes /></Suspense>} />
                    <Route path="tests" element={<Navigate to="/admin/questionari/tests" replace />} />
                    <Route path="analytics" element={<Suspense fallback={<AdminFallback />}><AdminLearningAnalytics /></Suspense>} />
                    <Route path="ai" element={<Suspense fallback={<AdminFallback />}><AdminAI /></Suspense>} />
                  </Route>
                  <Route path="questionari" element={<Suspense fallback={<AdminFallback />}><AdminQuestionari /></Suspense>} />
                  <Route path="questionari/tests" element={<Suspense fallback={<AdminFallback />}><AdminTests /></Suspense>} />
                  <Route path="questionari/:cid" element={<Suspense fallback={<AdminFallback />}><AdminQuestionarioEditor /></Suspense>} />
                  <Route path="questionari/:cid/assignments" element={<Suspense fallback={<AdminFallback />}><AdminQuestionarioAssignments /></Suspense>} />
                  <Route path="questionari/:cid/results" element={<Suspense fallback={<AdminFallback />}><AdminQuestionarioResults /></Suspense>} />
                  <Route path="cestino" element={<Suspense fallback={<AdminFallback />}><AdminCestino /></Suspense>} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </FavoritesProvider>
      </AuthProvider>
      </AppCacheProvider>
    </div>
  );
}

function LegacyTestRedirect() { const { id } = useParams(); return <Navigate to={`/questionari/test/${id}`} replace />; }
